// "Add Goal Type" tiles for the Flow2 inline Goals table. Clicking a tile starts an
// inline create row of that goal type. Reused both in the page header (with goals present)
// and inside the empty-state card. Presentational only.
import { styled, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FLOW2_CATEGORY_GOALS } from '../../common/constants';
import { Flow2GoalTypeProps } from '../../common/types/clients-entity';
import { Icon, Typography } from '../../components/atoms';
import { GoalTypeIconSmall } from '../../themes/styles/common';
import { GoalRowType } from './goalInlineHelpers';

const TilesRow = styled('div')(() => ({
    // One row of equal-width columns sized to the widest tile, so all three stay on a
    // single line and no subtitle wraps (even the longer locales).
    display: 'inline-grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    gap: '8px',
    maxWidth: '100%'
}));

const Tile = styled('button')<{ accent?: string; hoverBg?: string; disabled?: boolean }>(
    ({ theme, accent, hoverBg, disabled }) => ({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '12px',
        borderRadius: '8px',
        border: `1px solid ${accent || theme.palette.grey[200]}`,
        backgroundColor: theme.palette.neutral.white,
        // Subtitle reads this var so hover/focus can darken it (Typography applies its color inline).
        '--goal-tile-subtitle-color': theme.palette.semantic.text.muted,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        textAlign: 'left',
        // Fill the equal grid cell so every tile is the same width.
        width: 'auto',
        minWidth: 0,
        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
        // Keyboard focus mirrors hover so tabbing gets the same tint/subtitle affordance (native focus ring kept).
        '&:hover, &:focus-visible': disabled
            ? {}
            : {
                  // Tile fills with the goal's tint and the subtitle darkens (Figma).
                  backgroundColor: hoverBg || theme.palette.neutral.white,
                  boxShadow: theme.shadows[2],
                  '--goal-tile-subtitle-color': theme.palette.semantic.text.default
              }
    })
);

const TileText = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
}));

const TILE_SUBTITLE_KEY: Record<GoalRowType, string> = {
    income: 'GOAL_TILE_INCOME_SUBTITLE',
    wealth: 'GOAL_TILE_WEALTH_SUBTITLE',
    retirement: 'GOAL_TILE_RETIREMENT_SUBTITLE'
};

const TILE_TITLE_KEY: Record<GoalRowType, string> = {
    income: 'INCOME_GOAL_CARD_HEADER',
    wealth: 'WEALTH_GOAL_CARD_HEADER',
    retirement: 'RETIREMENT_GOAL_CARD_HEADER'
};

interface GoalTypeTilesProps {
    // eslint-disable-next-line no-unused-vars
    onSelect: (type: GoalRowType) => void;
    disabled?: boolean;
}

export const GoalTypeTiles = ({ onSelect, disabled = false }: GoalTypeTilesProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    return (
        <TilesRow>
            {FLOW2_CATEGORY_GOALS.map((g: Flow2GoalTypeProps) => {
                const type = g.type as GoalRowType;
                return (
                    <Tile
                        key={type}
                        type="button"
                        accent={g.color}
                        hoverBg={g.backgroundColor}
                        disabled={disabled}
                        aria-disabled={disabled}
                        onClick={() => !disabled && onSelect(type)}
                        data-testid={`add-goal-tile-${type}`}
                    >
                        <GoalTypeIconSmall color={g.color} backgroundColor={g.backgroundColor}>
                            <Icon id={type} size={24} />
                        </GoalTypeIconSmall>
                        <TileText>
                            <Typography
                                label={t(TILE_TITLE_KEY[type])}
                                variant="body.large"
                                sx={{
                                    fontWeight: 400,
                                    fontSize: '16px',
                                    lineHeight: '22px',
                                    color: theme.palette.semantic.int.selected
                                }}
                            />
                            <Typography
                                label={t(TILE_SUBTITLE_KEY[type])}
                                variant="labels.small"
                                sx={{ color: 'var(--goal-tile-subtitle-color)', whiteSpace: 'nowrap' }}
                            />
                        </TileText>
                    </Tile>
                );
            })}
        </TilesRow>
    );
};

/** Small circular goal-type badge used inside the Name cell (icon + type label). */
export const GoalTypeBadge = ({ type }: { type: GoalRowType }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = FLOW2_CATEGORY_GOALS.find((x: any) => x.type === type);
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: g?.backgroundColor,
                color: g?.color,
                flexShrink: 0
            }}
        >
            <Icon id={type} size={16} />
        </span>
    );
};
